import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';

const NoteTemplateManagementPage = ({ templateId: propTemplateId, onClose, onSave, isEmbedded = false }) => {
  const { templateId: paramTemplateId } = useParams();
  const navigate = useNavigate();
  const templateId = propTemplateId || paramTemplateId;
  const isEditMode = !!templateId;

  const [template, setTemplate] = useState({
    name: '',
    description: '',
    template_type: 'SOAP',
    content: '',
    is_active: true,
    provider: null,
    health_systems: []
  });
  const [healthSystems, setHealthSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Template type options
  const templateTypes = [
    { value: 'SOAP', label: 'SOAP Note' },
    { value: 'H&P', label: 'History & Physical' },
    { value: 'Progress', label: 'Progress Note' },
    { value: 'Discharge', label: 'Discharge Summary' },
    { value: 'Consultation', label: 'Consultation Note' },
    { value: 'Procedure', label: 'Procedure Note' },
    { value: 'Operative', label: 'Operative Note' },
    { value: 'Nurse', label: 'Nurse Note' },
    { value: 'Social Worker', label: 'Social Worker Note' },
    { value: 'Other', label: 'Other' }
  ];

  // Load template data if editing
  useEffect(() => {
    if (isEditMode) {
      const fetchTemplate = async () => {
        try {
          setLoading(true);
          const response = await adminApi.get(`/admin/note/templates/${templateId}/`);
          if (response.data) {
            // Extract health_system IDs from the response (API returns objects with {id, name})
            const healthSystemIds = response.data.health_systems?.map(hs => hs.id || hs) || [];
            setTemplate({
              ...response.data,
              health_systems: healthSystemIds
            });
          } else {
            setError('Template not found');
          }
        } catch (err) {
          console.error('Error fetching template:', err);
          setError(`Error: ${err.message}`);
        } finally {
          setLoading(false);
        }
      };

      fetchTemplate();
    }
  }, [templateId, isEditMode]);

  // Fetch health systems
  useEffect(() => {
    const fetchHealthSystems = async () => {
      try {
        const response = await adminApi.get('/admin/health-systems/');
        if (response.data && response.data.health_systems) {
          setHealthSystems(response.data.health_systems);
        }
      } catch (err) {
      null;
      }
    };

    fetchHealthSystems();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTemplate(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle health system checkbox changes
  const handleHealthSystemToggle = (healthSystemId) => {
    setTemplate(prev => {
      const currentHealthSystems = prev.health_systems || [];
      const isSelected = currentHealthSystems.includes(healthSystemId);

      return {
        ...prev,
        health_systems: isSelected
          ? currentHealthSystems.filter(id => id !== healthSystemId)
          : [...currentHealthSystems, healthSystemId]
      };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!template.name.trim()) {
      setError('Template name is required');
      return;
    }

    if (!template.content.trim()) {
      setError('Template content is required');
      return;
    }

    if (!template.health_systems || template.health_systems.length === 0) {
      setError('Please select at least one healthcare organization');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEditMode) {
        // Update existing template
        await adminApi.put(`/admin/note/templates/${templateId}/`, template);
      } else {
        // Create new template
        await adminApi.post('/admin/note/templates/', template);
      }

      // Navigate back or call onSave callback
      if (isEmbedded && onSave) {
        onSave();
      } else {
        navigate('/admin/note-templates');
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError(`Error saving template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (isEmbedded && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading template...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={isEmbedded ? '' : 'container-fluid'}>
        {!isEmbedded && (
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">
                  <button
                    className="btn btn-sm btn-light me-2"
                    onClick={handleBack}
                  >
                    <i className="fa fa-arrow-left me-1"></i>
                  </button>
                  {isEditMode ? 'Edit Note Template' : 'Create New Note Template'}
                </h4>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="row">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-primary mb-4">Template Details</h5>
                
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="name" className="form-label">
                        Template Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={template.name}
                        onChange={handleInputChange}
                        placeholder="Enter template name"
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label htmlFor="template_type" className="form-label">
                        Template Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="template_type"
                        name="template_type"
                        value={template.template_type}
                        onChange={handleInputChange}
                        required
                      >
                        {templateTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      rows="3"
                      value={template.description}
                      onChange={handleInputChange}
                      placeholder="Enter template description (optional)"
                    />
                  </div>

                  {/* Health Systems Multi-Select */}
                  <div className="mb-3">
                    <label className="form-label">
                      Healthcare Organizations <span className="text-danger">*</span>
                    </label>
                    <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {healthSystems && healthSystems.length > 0 ? (
                        healthSystems.map(healthSystem => (
                          <div key={healthSystem.id} className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`hs-${healthSystem.id}`}
                              checked={(template.health_systems || []).includes(healthSystem.id)}
                              onChange={() => handleHealthSystemToggle(healthSystem.id)}
                            />
                            <label className="form-check-label" htmlFor={`hs-${healthSystem.id}`}>
                              {healthSystem.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <div className="text-muted">Loading healthcare organizations...</div>
                      )}
                    </div>
                    <div className="form-text">
                      Select the healthcare organization(s) where this template will be available.
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="content" className="form-label">
                      Template Content <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      id="content"
                      name="content"
                      rows="15"
                      value={template.content}
                      onChange={handleInputChange}
                      placeholder="Enter the template content with placeholders like {patient_name}, {chief_complaint}, etc."
                      required
                    />
                    <div className="form-text">
                      Use placeholders like {`{{patient_name}}`}, {`{{chief_complaint}}`}, {`{{assessment}}`}, {`{{plan}}`} to create dynamic templates.
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={template.is_active}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="is_active">
                        Active Template
                      </label>
                      <div className="form-text">
                        Only active templates will be available for use in note generation.
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {isEditMode ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          {isEditMode ? 'Update Template' : 'Create Template'}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleBack}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-primary mb-4">Template Guidelines</h5>
                
                <div className="mb-4">
                  <h6 className="text-primary">Available Placeholders:</h6>
                  <ul className="list-unstyled">
                    <li><code>{`{{patient_name}}`}</code> - Patient&apos;s full name</li>
                    <li><code>{`{{patient_age}}`}</code> - Patient&apos;s age</li>
                    <li><code>{`{{patient_dob}}`}</code> - Date of birth</li>
                    <li><code>{`{{chief_complaint}}`}</code> - Chief complaint</li>
                    <li><code>{`{{history_present_illness}}`}</code> - HPI</li>
                    <li><code>{`{{physical_exam}}`}</code> - Physical examination</li>
                    <li><code>{`{{assessment}}`}</code> - Assessment</li>
                    <li><code>{`{{plan}}`}</code> - Treatment plan</li>
                    <li><code>{`{{provider_name}}`}</code> - Provider&apos;s name</li>
                    <li><code>{`{{date}}`}</code> - Current date</li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h6 className="text-primary">Template Types:</h6>
                  <ul className="list-unstyled">
                    <li><strong>SOAP:</strong> Subjective, Objective, Assessment, Plan</li>
                    <li><strong>H&P:</strong> History and Physical examination</li>
                    <li><strong>Progress:</strong> Progress notes for follow-up visits</li>
                    <li><strong>Discharge:</strong> Discharge summaries</li>
                    <li><strong>Consultation:</strong> Specialist consultation notes</li>
                  </ul>
                </div>

                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Tip:</strong> Use clear, structured templates with appropriate placeholders to ensure consistent and comprehensive documentation.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NoteTemplateManagementPage;